'use client'

/**
 * CleanProfileFrame — the avatar in a tier frame, everywhere it appears (crest, dropdown, chat,
 * rankings, lists).
 *
 * ⭐ 2026-08-22: NOW RENDERS THE SHARED LEDGER FRAME (src/styles/ledger-frame.css), the SAME frame the
 * Order of the Ledger on /donate uses, so the crest and the ledger are literally identical (owner:
 * "it should look like the ledger ... it should be the same"). This replaces the old split where the
 * navbar drew a flat rail ring and /donate drew ornate per-tier CSS — the very difference the owner
 * flagged.
 *
 * The ring is a conic gradient drawn behind a slightly-smaller photo (only the rim shows), plus a
 * soft halo — crisp at any size, no "second circle". Motion is OPT-IN via `animated`: the crest and
 * the /donate showcase pass it (ring rotates, halo breathes); list avatars leave it off so a feed of
 * dozens does not spin. reduced-motion disables it regardless.
 */
import Image from 'next/image'

const DEFAULT_AVATAR = '/assets/logo/thedesperatetrader-logo.svg'

const TIER_NAMES: Record<string, string> = {
  none: 'No Frame', tier1: 'Neophyte', tier2: 'Patron', tier3: 'Steward', tier4: 'Factor',
  tier5: 'Quaestor', tier6: 'Warden', tier7: 'Magnate', tier8: 'Archon', tier9: 'Exarch', tier10: 'Sovereign',
}

/** Top tiers earn a counter-spinning sparkle layer (only when animated). */
const SPARK_TIERS = new Set(['tier8', 'tier9', 'tier10'])

/** Strip shadow/scale/ring/hover classes a caller passes — the frame owns its own ring+halo, and
 *  those would paint a second, misaligned circle (the original navbar bug). */
function sanitizeWrapper(cn: string): string {
  return cn
    .split(/\s+/)
    .filter(Boolean)
    .filter(
      (t) =>
        !t.startsWith('shadow') &&
        !t.startsWith('ring-') &&
        !t.includes('scale') &&
        !t.startsWith('transition') &&
        !t.startsWith('duration') &&
        !t.startsWith('group-hover:') &&
        !t.startsWith('hover:'),
    )
    .join(' ')
}

interface CleanProfileFrameProps {
  tier: string
  src?: string | null
  alt: string
  width: number
  height: number
  className?: string
  showTierName?: boolean
  /** Rotate the ring + breathe the halo. On for hero placements (crest, /donate); off in lists. */
  animated?: boolean
}

export default function CleanProfileFrame({
  tier,
  src,
  alt,
  width,
  height,
  className = '',
  showTierName = false,
  animated = false,
}: CleanProfileFrameProps) {
  const imageSrc: string = typeof src === 'string' && src.trim() ? src : DEFAULT_AVATAR
  const tierKey = (tier || 'none').toLowerCase()
  const size = width
  // Ring thickness scales with size (2px at 24px → 4px cap), photo sits inside it.
  const ring = Math.min(4, Math.max(2, Math.round(size * 0.09)))
  const photo = Math.max(8, size - ring * 2)
  const live = animated && tierKey !== 'none'
  const spark = live && SPARK_TIERS.has(tierKey)
  const wrap = sanitizeWrapper(className)
  // Small avatars: a native img avoids the Next/Image wrapper misaligning inside the grid. Larger
  // ones (profile headers) keep Next/Image for optimisation.
  const useNative = size <= 48

  return (
    <div
      className={`ledger-frame lf-${tierKey}${live ? ' lf-live' : ''}${wrap ? ' ' + wrap : ''}`}
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
    >
      {spark && <span className="lf-spark" aria-hidden />}
      {useNative ? (
        // eslint-disable-next-line @next/next/no-img-element -- native img keeps the photo centered in the ring at small sizes
        <img
          className="lf-photo"
          src={imageSrc}
          alt={alt}
          width={photo}
          height={photo}
          style={{ width: photo, height: photo }}
          draggable={false}
          decoding="async"
          // 2026-10-22 (register mobile-first-paint-1021): lazy, so avatars in a HIDDEN tab (the stock
          // page Community panel, ~20 of them) are not fetched at page load. On-screen and near-screen
          // avatars, like the header crest, still load at once.
          loading="lazy"
        />
      ) : (
        <Image className="lf-photo" src={imageSrc} alt={alt} width={photo} height={photo} style={{ width: photo, height: photo }} />
      )}
      <span className="lf-sheen" aria-hidden />
      {showTierName && (
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 transform text-center">
          <div className="rounded bg-background/90 px-2 py-1 text-xs font-medium text-foreground">
            {TIER_NAMES[tierKey] || 'Unknown'}
          </div>
        </div>
      )}
    </div>
  )
}
